import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Users, Loader2, CheckCircle, XCircle, BookOpen, Tv } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SortaLogo = ({ size = "default" }) => {
  const sizeClasses = { small: "h-8", default: "h-10", large: "h-14" };
  return (
    <img 
      src="https://customer-assets.emergentagent.com/job_series-ranker-1/artifacts/barwtr5h_image.jpg"
      alt="Sorta"
      className={`${sizeClasses[size]} object-contain rounded-lg`}
    />
  );
};

export default function JoinListPage() {
  const { shareCode } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, error, auth_required
  const [listInfo, setListInfo] = useState(null);
  const [error, setError] = useState(null);
  
  // Check if this is a book list
  const listType = searchParams.get('type') || 'watchlist'; // 'watchlist' or 'book'
  const isBookList = listType === 'book';

  useEffect(() => {
    const checkAndJoinList = async () => {
      try {
        // Use appropriate endpoint based on list type
        const infoEndpoint = isBookList 
          ? `${API}/book-lists/share/${shareCode}/info`
          : `${API}/watchlists/share/${shareCode}/info`;
        const joinEndpoint = isBookList
          ? `${API}/book-lists/share/${shareCode}/join`
          : `${API}/watchlists/share/${shareCode}/join`;
        
        // First, try to get list info (public endpoint)
        const infoRes = await axios.get(infoEndpoint);
        setListInfo(infoRes.data);

        // Then try to join (requires auth)
        const joinRes = await axios.post(
          joinEndpoint,
          {},
          { withCredentials: true }
        );
        
        setStatus('success');
        toast.success(`You've joined "${infoRes.data.title}"!`);
        
        // Redirect to the list after a short delay
        const listId = isBookList ? joinRes.data.list_id : joinRes.data.watchlist_id;
        const redirectPath = isBookList ? `/book-list/${listId}` : `/watchlist/${listId}`;
        setTimeout(() => {
          navigate(redirectPath);
        }, 2000);
      } catch (err) {
        if (err.response?.status === 401) {
          setStatus('auth_required');
        } else if (err.response?.status === 400 && err.response?.data?.detail?.includes('already')) {
          // User already in list, redirect
          setStatus('success');
          toast.info('You are already a member of this list');
          setTimeout(() => {
            navigate(isBookList ? '/books' : '/dashboard');
          }, 1500);
        } else {
          setStatus('error');
          setError(err.response?.data?.detail || 'Failed to join list');
        }
      }
    };
    
    checkAndJoinList();
  }, [shareCode, navigate, isBookList]);

  const handleLoginToJoin = () => {
    // Store the share URL to redirect back after login
    sessionStorage.setItem('pendingJoinUrl', window.location.pathname + window.location.search);
    navigate('/');
  };

  const ListIcon = isBookList ? BookOpen : Tv;
  const listTypeLabel = isBookList ? 'reading list' : 'watchlist';

  return (
    <div className="min-h-screen bg-[#3D4F6F] flex items-center justify-center p-6">
      <Card className="max-w-md w-full border-0 shadow-xl bg-white/95 backdrop-blur-sm">
        <CardContent className="p-8 text-center">
          <div className="mb-6 flex justify-center">
            <SortaLogo size="large" />
          </div>

          {status === 'loading' && (
            <>
              <Loader2 className="w-12 h-12 text-[#F87171] animate-spin mx-auto mb-4" />
              <h2 className="font-outfit text-xl font-semibold text-[#3D4F6F] mb-2">
                Joining List...
              </h2>
              <p className="text-[#64748B]">Please wait while we add you to this list</p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="font-outfit text-xl font-semibold text-[#3D4F6F] mb-2">
                Successfully Joined!
              </h2>
              {listInfo && (
                <p className="text-[#64748B] mb-4">
                  You've been added to <strong>"{listInfo.title}"</strong>
                </p>
              )}
              <p className="text-sm text-[#94A3B8]">Redirecting to your list...</p>
            </>
          )}

          {status === 'auth_required' && (
            <>
              <ListIcon className="w-16 h-16 text-[#F87171] mx-auto mb-4" />
              <h2 className="font-outfit text-xl font-semibold text-[#3D4F6F] mb-2">
                You're Invited!
              </h2>
              {listInfo && (
                <div className="mb-6">
                  <p className="text-[#64748B] mb-2">
                    <strong>{listInfo.owner_name}</strong> invited you to join their {listTypeLabel}:
                  </p>
                  <p className="font-outfit text-lg font-semibold text-[#3D4F6F]">
                    "{listInfo.title}"
                  </p>
                  {listInfo.description && (
                    <p className="text-sm text-[#94A3B8] mt-1">{listInfo.description}</p>
                  )}
                  <p className="text-sm text-[#64748B] mt-2">
                    {listInfo.book_count || listInfo.series_count} {isBookList ? 'books' : 'items'} • {listInfo.member_count} members
                  </p>
                </div>
              )}
              <Button
                onClick={handleLoginToJoin}
                className={`w-full ${isBookList ? 'bg-amber-500 hover:bg-amber-600' : 'bg-[#F87171] hover:bg-[#EF4444]'} text-white rounded-full`}
                data-testid="login-to-join-btn"
              >
                Sign in to Join
              </Button>
              <p className="text-xs text-[#94A3B8] mt-4">
                Sign in with Google to start {isBookList ? 'reading' : 'ranking'} together
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="font-outfit text-xl font-semibold text-[#3D4F6F] mb-2">
                Unable to Join
              </h2>
              <p className="text-[#64748B] mb-6">{error}</p>
              <Button
                onClick={() => navigate(isBookList ? '/books' : '/tv-shows')}
                className={`${isBookList ? 'bg-amber-500 hover:bg-amber-600' : 'bg-[#F87171] hover:bg-[#EF4444]'} text-white rounded-full`}
              >
                Go to My Lists
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
